import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { cn } from "../utils";
import { useCoverageDisplayContext } from "../contexts/CoverageDisplayContext";
import { getIllnessSubSections } from "./FullCoverageDetailsModal/coverageData";
import { Button } from "./ui/Button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/shared/components/ui/accordion";

import { faShield, faWarning } from "@fortawesome/pro-regular-svg-icons";

interface IllnessSectionGridProps {
    onSwitchToAccidentAndIllness?: () => void;
}

export function IllnessSectionGrid({ onSwitchToAccidentAndIllness }: IllnessSectionGridProps) {
    const { illnessSections } = useCoverageDisplayContext();

    const coverageIconColSize = "shrink-0 w-9 xs:w-12 sm:w-9 md:w-12 lg:w-9 xl:w-12";

    const sectionsToDisplay = Object.keys(illnessSections).length > 0 ? illnessSections : getIllnessSubSections("size-9 sm:size-12");

    const illnessCoverageData = Object.entries(sectionsToDisplay).map(([key, section]) => (
        <AccordionItem value={key} className="border-b-0" key={key}>
            <AccordionTrigger id={`accordion-trigger-${key}`} className="py-0 hover:no-underline">
                <div className="flex flex-row items-center gap-2 text-left text-sm text-content-secondary xs:text-base sm:gap-4 sm:text-sm md:gap-1 md:text-base lg:gap-4 lg:text-sm xl:text-base">
                    <div className={cn(coverageIconColSize, "relative")}>{section.icon}</div>
                    {section.title}
                </div>
            </AccordionTrigger>
            <AccordionContent aria-labelledby={`accordion-trigger-${key}`} className="grid gap-2 py-0 pt-2 sm:grid-cols-2">
                {section.includes?.map(({ title }) => (
                    <div
                        key={title}
                        className="flex flex-row items-center gap-2 text-sm font-medium text-content-secondary xs:text-base sm:text-sm md:text-base md:leading-5 lg:text-sm xl:text-base xl:leading-5"
                    >
                        <div className={cn("flex shrink-0 items-center justify-center text-content-primary", coverageIconColSize)}>
                            <FontAwesomeIcon icon={faCheck} className="size-5" />
                        </div>
                        <div className="flex flex-wrap gap-1">{title}</div>
                    </div>
                ))}
            </AccordionContent>
        </AccordionItem>
    ));

    return (
        <div data-testid="illness-section-grid" className="border-t border-stroke-secondary">
            <div className="mt-4 rounded-lg bg-background-warning p-4">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="inline-flex w-fit gap-2 text-center text-base font-bold text-content-warning sm:items-center sm:text-left sm:text-sm md:text-base lg:text-sm xl:text-base">
                        <div className="flex shrink-0 justify-center">
                            <FontAwesomeIcon className="size-5" icon={faWarning} />
                        </div>
                        <div>What you miss without Illness coverage</div>
                    </div>

                    <Button
                        data-testid="btn-get-illness-coverage"
                        variant="default-rounded"
                        size="md"
                        className={cn("hidden w-[210px] px-3 py-2 text-sm sm:block")}
                        startDecorator={<FontAwesomeIcon icon={faShield} className="size-5" />}
                        onClick={onSwitchToAccidentAndIllness}
                    >
                        Get illness coverage
                    </Button>
                </div>

                {/* Desktop (xl+) - Grid layout */}
                <div className="hidden xl:grid xl:grid-cols-3 xl:gap-2">
                    {Object.entries(sectionsToDisplay).map(([key, section]) => (
                        <div key={key} className="flex flex-col gap-2" role="region" aria-labelledby={`illness-section-${key}-heading`}>
                            <div
                                id={`illness-section-${key}-heading`}
                                className="flex flex-row items-center gap-2 text-left text-sm font-bold text-content-secondary xs:text-base sm:text-sm md:text-base lg:text-sm xl:text-base"
                            >
                                <div className={cn(coverageIconColSize)}>{section.icon}</div>
                                {section.title}
                            </div>
                            <div className="flex flex-col gap-2">
                                {section.includes?.map(({ title }) => (
                                    <div
                                        key={title}
                                        className="flex flex-row items-center gap-2 text-sm font-medium text-content-secondary xs:text-base sm:text-sm md:text-base md:leading-5 lg:text-sm xl:text-base xl:leading-5"
                                    >
                                        <div className={cn("flex shrink-0 items-center justify-center text-content-primary", coverageIconColSize)}>
                                            <FontAwesomeIcon icon={faCheck} className="size-5" />
                                        </div>
                                        <div className="flex flex-wrap gap-1">{title}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Mobile/Tablet (<xl) - Accordion layout */}
                <Accordion type="multiple" defaultValue={[]} className="flex flex-col gap-2 xs:gap-3 sm:gap-2 md:gap-3 lg:gap-2 xl:hidden">
                    {illnessCoverageData}
                </Accordion>
                <Button
                    data-testid="btn-get-illness-coverage"
                    variant="default-rounded"
                    size="md"
                    className={cn("mx-auto mt-3 block w-[210px] px-3 py-2 text-sm sm:hidden")}
                    startDecorator={<FontAwesomeIcon icon={faShield} className="size-5" />}
                    onClick={onSwitchToAccidentAndIllness}
                >
                    Get illness coverage
                </Button>
            </div>
        </div>
    );
}
